import { Pill, RiCloseLine, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetDraftPlotGroups } from '@/api/rest/resources/plot';
import { DraftPlot, DraftPlotGroup } from '@/api/rest/resources/types/plot';

import { useUpdateDraftPlotGroup } from '../hooks/useDraftPlotGroupActions';
import { AddToGroupDialog } from './AddToGroupDialog';

type GroupPickerProps = {
  plot: DraftPlot;
  organizationId: string;
};

export const GroupPicker = ({ plot, organizationId }: GroupPickerProps) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [removeError, setRemoveError] = useState(false);

  const { data: groupsData, refetch } = useGetDraftPlotGroups({
    queryParams: { organization: organizationId },
  });
  const groups = groupsData?.results ?? [];
  const currentGroup = groups.find((group) => group.draft_plot_ids.includes(plot.id));

  const { submit: updateGroup, isSubmitting: isRemoving } = useUpdateDraftPlotGroup();

  const handleRemove = async (group: DraftPlotGroup) => {
    setRemoveError(false);
    try {
      await updateGroup(group.id, {
        draft_plot_ids: group.draft_plot_ids.filter((id) => id !== plot.id),
      });
    } catch {
      setRemoveError(true);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction='row' className='flex-wrap items-center gap-2'>
        {currentGroup ? (
          <Pill size='small' className='flex items-center gap-1 bg-neutral-black-8 text-text-primary'>
            <span>{currentGroup.name}</span>
            <button
              type='button'
              aria-label={t('shared.uploadPlots.groupPicker.removeAriaLabel', { name: currentGroup.name })}
              onClick={() => handleRemove(currentGroup)}
              disabled={isRemoving}
              className='text-text-secondary hover:text-text-primary'
            >
              <RiCloseLine size={12} />
            </button>
          </Pill>
        ) : (
          <button
            type='button'
            onClick={() => setIsDialogOpen(true)}
            className='typography-body2Semibold rounded-full border border-dashed border-neutral-black-23 px-3 py-0.5 text-text-secondary hover:border-primary-100 hover:text-primary-100'
          >
            {t('shared.uploadPlots.groupPicker.addButton')}
          </button>
        )}
      </Stack>
      {removeError && (
        <span className='typography-caption text-error'>{t('shared.uploadPlots.groupPicker.removeFailed')}</span>
      )}

      <AddToGroupDialog
        open={isDialogOpen}
        organizationId={organizationId}
        groups={groups}
        plotIds={[plot.id]}
        onClose={() => setIsDialogOpen(false)}
        onSaved={() => {
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </Stack>
  );
};
